import get from 'lodash/get';
import forEach from 'lodash/forEach';
import {v4 as uuidV4} from 'uuid';
import {getWindow} from 'ssr-window';
import SentryUtils from '@spothero/utils/sentry';
import CityAPI from 'api/city';
import DestinationAPI from 'api/destination';
import EventsAPI from 'api/events';
import AirportAPI from 'api/airport';
import ExperimentUtils from 'utils/experiment';
import {initialState as cityInitialState} from 'city/city-reducer';
import {initialState as destinationInitialState} from 'destination/destination-reducer';
import {initialState as eventInitialState} from 'event/event-reducer';
import {Page} from 'page/utils';

const window = getWindow();

const SearchRequestUtils = {
    sanitizeSearchRequestResponse({state, response}) {
        const {
            monthly: isMonthly,
            airport,
            page_info: {page_type: pageType, setup},
        } = response;
        const mappedData = {};
        const isSearchPage =
            pageType === Page.SEARCH ||
            pageType === Page.CITY ||
            pageType === Page.DESTINATION ||
            pageType === Page.EVENT;
        const isAirport =
            (pageType === Page.DESTINATION && setup.destination.is_airport) ||
            airport;

        forEach(state, (value, key) => {
            mappedData[key] = response[key] || value;
        });

        ExperimentUtils.setUserAttributes({
            monthly: Boolean(isMonthly).toString(),
        });

        return {
            ...mappedData,
            pageType,
            isSearchPage,
            id:
                pageType === Page.CHECKOUT ||
                pageType === Page.FACILITY ||
                isSearchPage
                    ? uuidV4()
                    : null,
            airport: !isMonthly && isAirport,
        };
    },

    async loadDataSequence({pageType, setup, isNewCity = false}) {
        const setupData = {
            destination: destinationInitialState(),
            event: eventInitialState(),
        };
        const promises = [
            Promise.resolve(), // city
            Promise.resolve(), // destination
            Promise.resolve(), // event
            Promise.resolve(), // airport
        ];
        const {
            city: {slug: citySlug},
        } = setup;

        // if the city slug is different than what is currently saved in the store,
        // load the new city information as well
        if (isNewCity) {
            setupData.city = cityInitialState();
            promises[0] = CityAPI.get(citySlug);
        }

        if (pageType === Page.DESTINATION || pageType === Page.EVENT) {
            promises[1] = setup.destination.slug
                ? DestinationAPI.getBySlug(citySlug, setup.destination.slug)
                : DestinationAPI.get(setup.destination.id);

            if (pageType === Page.EVENT) {
                promises[2] = EventsAPI.getById(setup.event.id);
            }
        }

        if (pageType === Page.AIRPORT) {
            const {
                destination: {slug},
            } = setup;

            promises[3] = AirportAPI.getByCode(slug);
        }

        try {
            const [city, destination, event, airport] = await Promise.all(
                promises
            );

            if (city) {
                setupData.city.data = city.data.data;
            }

            if (destination) {
                setupData.destination.data = destination.data.data;
            }

            if (event) {
                setupData.event.data = event.data.data;
            }

            if (airport) {
                setupData.destination.data = airport.data.data;
            }

            return setupData;
        } catch (error) {
            const errorMessage =
                error &&
                (error.message ||
                    error.statusText ||
                    get(error, 'response.data', ''));

            SentryUtils.addData({caughtError: error});
            SentryUtils.sendMessage({message: errorMessage, level: 'error'});

            if (pageType === Page.EVENT) {
                // in case there are any errors loading event data,
                // redirect the page to the destination URL
                window.location = `/${citySlug}/${setupData.destination.data.relative_url}`;
            }

            throw error;
        }
    },
};

export default SearchRequestUtils;
